import { OwnCapability } from '@stream-io/video-client';
import { Restricted, useCallStateHooks, useI18n } from '@stream-io/video-react-bindings';
import {
  DeviceSelectorAudioInput,
  PermissionNotification,
  useRequestPermission,
  WithTooltip,
} from '@stream-io/video-react-sdk';
import { Mic, MicOff } from 'lucide-react';
import { useState } from 'react';
import { createCallControlHandler } from './utils';
import { CompositeButton } from './composite-button';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ToggleAudioPublishingButton = (props: any) => {
  const { t } = useI18n();
  const {
    caption,
    Menu = <DeviceSelectorAudioInput visualType="list" />,
    menuPlacement = 'top',
    onMenuToggle,
    ...restCompositeButtonProps
  } = props;

  const { hasPermission, requestPermission, isAwaitingPermission } = useRequestPermission(
    OwnCapability.SEND_AUDIO
  );

  const { useMicrophoneState } = useCallStateHooks();
  const { microphone, optimisticIsMute, hasBrowserPermission } = useMicrophoneState();
  const [tooltipDisabled, setTooltipDisabled] = useState(false);
  const handleClick = createCallControlHandler(props, async () => {
    if (!hasPermission) {
      await requestPermission();
    } else {
      await microphone.toggle();
    }
  });

  return (
    <Restricted requiredGrants={[OwnCapability.SEND_AUDIO]}>
      <PermissionNotification
        permission={OwnCapability.SEND_AUDIO}
        isAwaitingApproval={isAwaitingPermission}
        messageApproved={t('You can now speak.')}
        messageAwaitingApproval={t('Awaiting for an approval to speak.')}
        messageRevoked={t('You can no longer speak.')}
      >
        <WithTooltip
          title={
            !hasPermission
              ? t('You have no permission to share your audio')
              : !hasBrowserPermission
                ? t('Check your browser mic permissions')
                : (caption ?? t('Mic'))
          }
          tooltipDisabled={tooltipDisabled}
        >
          <CompositeButton
            active={optimisticIsMute}
            caption={caption}
            variant="secondary"
            disabled={!hasBrowserPermission || !hasPermission}
            data-testid={optimisticIsMute ? 'audio-unmute-button' : 'audio-mute-button'}
            onClick={handleClick}
            Menu={Menu}
            menuPlacement={menuPlacement}
            menuOffset={16}
            {...restCompositeButtonProps}
            onMenuToggle={(shown) => {
              setTooltipDisabled(shown);
              onMenuToggle?.(shown);
            }}
          >
            {optimisticIsMute ? (
              <MicOff className="w-5 h-5 text-accent" />
            ) : (
              <Mic className="w-5 h-5 text-accent" />
            )}
            {(!hasBrowserPermission || !hasPermission) && (
              <span className="str-video__no-media-permission">!</span>
            )}
          </CompositeButton>
        </WithTooltip>
      </PermissionNotification>
    </Restricted>
  );
};
